import { useState } from "react";
import { Sparkles, Building2, HandHeart, Trophy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const TIERS = [
  {
    name: "Community",
    price: "$500/mo",
    icon: HandHeart,
    perks: ["Logo on /sponsorships", "Quarterly newsletter mention", "1 sidebar placement"],
  },
  {
    name: "Catalyst",
    price: "$2,000/mo",
    icon: Building2,
    perks: ["Everything in Community", "Featured sponsor block sitewide", "Co-branded insights post"],
  },
  {
    name: "Anchor",
    price: "$5,000+/mo",
    icon: Trophy,
    perks: ["Everything in Catalyst", "Anchor placement on /ideas", "Quarterly impact report", "Custom campaign support"],
  },
];

export default function Sponsorships() {
  const [form, setForm] = useState({ name: "", email: "", org: "", message: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.email || !form.name) {
      toast.error("Please add your name and email");
      return;
    }
    setBusy(true);
    const { error } = await (supabase as any).from("signups").insert({
      email: form.email,
      name: form.name,
      organization: form.org,
      source: "sponsor_inquiry",
    });
    setBusy(false);
    if (error) {
      toast.error("Couldn't submit", { description: error.message });
      return;
    }
    toast.success("Thanks — we'll be in touch within 2 business days.");
    setForm({ name: "", email: "", org: "", message: "" });
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="container max-w-5xl mx-auto px-4 py-12 md:py-20">
        <div className="text-center mb-12">
          <Sparkles className="h-8 w-8 mx-auto text-primary mb-3" />
          <h1 className="font-display text-4xl md:text-5xl font-bold text-foreground mb-3">
            Sponsor the civic ecosystem
          </h1>
          <p className="text-muted-foreground max-w-xl mx-auto">
            Align your brand with the work of advocates, agencies, funders, and policymakers across Alabama.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-4 md:gap-6 mb-12">
          {TIERS.map((t) => (
            <div key={t.name} className="rounded-2xl border border-border bg-card p-6">
              <t.icon className="h-6 w-6 text-primary mb-3" />
              <h3 className="font-display text-xl font-semibold text-foreground">{t.name}</h3>
              <div className="text-2xl font-bold text-foreground mt-1 mb-4">{t.price}</div>
              <ul className="space-y-2 text-sm text-foreground/80">
                {t.perks.map((p) => (
                  <li key={p}>• {p}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <form onSubmit={submit} className="rounded-2xl border border-border bg-card p-6 md:p-8 max-w-xl mx-auto space-y-3">
          <h2 className="font-display text-2xl font-semibold text-foreground">Become a sponsor</h2>
          <p className="text-sm text-muted-foreground">Tell us about your organization.</p>
          <Input placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <Input type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <Input placeholder="Organization" value={form.org} onChange={(e) => setForm({ ...form, org: e.target.value })} />
          <Textarea placeholder="What are you hoping to achieve?" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
          <Button type="submit" disabled={busy} className="w-full">
            {busy ? "Sending…" : "Request sponsorship info"}
          </Button>
        </form>
      </div>
    </div>
  );
}